import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLoaderData, useRouteLoaderData } from "react-router-dom";
import { axiosInstance } from "../auth/Auth";
import moment from "moment/min/moment-with-locales";
import { getErrMsg, mapPlaylistPerspective, setNestedProperty } from "./HelperFunctions";
import { playlistPerspectives } from "./constants";
import { toast } from "react-toastify";

export function useSetPerspectiveOnDaysChange(setFormData, days) {
    useEffect(() => {
        setFormData(current => ({ ...current, perspective: JSON.stringify(current.days.map(x => Number(x)).sort()) }));
    }, [days, setFormData]);
}

export function useSetDaysOnPerspectiveChange(setFormData, perspective) {
    useEffect(() => {
        if (perspective && perspective !== "custom") {
            setFormData(current => ({ ...current, days: JSON.parse(perspective) }));
        }
    }, [setFormData, perspective]);
}

export function useSetCategoriesOnPlaylistPerspectiveChange(setFormData, perspectiveJson, perspectivesWithCategories) {
    useEffect(() => {
        async function setSongCategories() {
            const perspective = perspectivesWithCategories.find(x => JSON.stringify(x.categories.map(x => x.code)) === perspectiveJson);
            if (perspective) setFormData(current => ({ ...current, categories: perspective.categories }));
        }
        if (perspectiveJson !== "custom" && perspectivesWithCategories) setSongCategories();
    }, [setFormData, perspectivesWithCategories, perspectiveJson]);
}

export function useSetPlaylistPerspectiveOnCategoriesChange(setFormData, categories, perspectivesWithCategories) {
    useEffect(() => {
        const perspective = mapPlaylistPerspective(perspectivesWithCategories, categories);
        setFormData(current => ({
            ...current,
            playlistPerspective: JSON.stringify(perspective?.categories.map(x => x.code)),
        }));
    }, [categories, perspectivesWithCategories, setFormData]);
}

export function useLatestCalculatedDate(setLatestCalculatedDate, moduleId, station, demo) {
    const { latestCalculatedData } = useLoaderData();

    useEffect(() => {
        async function getLatestCalculatedDate() {
            if (station.length && demo.length) {
                const moduleData = (await latestCalculatedData).data.find(
                    x =>
                        x.module_id === moduleId[demo[0].data_type] &&
                        x.radio_id === station[0].radio_id &&
                        x.data_type === demo[0].data_type &&
                        Boolean(x.listener_group) === Boolean(demo[0].listener_group)
                );
                if (moduleData) setLatestCalculatedDate(moduleData.latest_datetime);
            }
        }
        getLatestCalculatedDate();
    }, [setLatestCalculatedDate, demo, station, moduleId, latestCalculatedData]);
}

export function useLatestDate(setFormData, date, latestCalculatedDate) {
    useEffect(() => {
        if (latestCalculatedDate && (date === undefined || date > new Date(latestCalculatedDate))) {
            setFormData(current => ({
                ...current,
                date: new Date(latestCalculatedDate),
            }));
        }
    }, [date, latestCalculatedDate, setFormData]);
}

export function useSetDaypartOnChange(setFormData, station, days, hourRange) {
    const { dayparts } = useLoaderData();

    useEffect(() => {
        async function handleHourRangeChange() {
            const radioId = station[0]?.radio_id;
            const daypart = (await dayparts).data.find(
                x =>
                    x.radio_id === radioId &&
                    JSON.stringify(days.sort()) === JSON.stringify(x.weekdays) &&
                    moment(x.start_time, "HH:mm:ss").hour() === hourRange[0] &&
                    (moment(x.end_time, "HH:mm:ss").hour() + 1 || 24) === hourRange[1]
            );
            setFormData(current => ({ ...current, daypart: daypart ? [daypart] : [] }));
        }
        handleHourRangeChange();
    }, [setFormData, hourRange, dayparts, station, days]);
}

export function useSetDataTypeOnStationChange(setFormData, station) {
    useEffect(() => {
        if (station[0]?.stream && !station[0]?.ppm) {
            setFormData(current => ({ ...current, dataType: 1 }));
        } else if (!station[0]?.stream && station[0]?.ppm) {
            setFormData(current => ({ ...current, dataType: 2 }));
        }
    }, [setFormData, station]);
}

export const useOnKeyEventPress = (callback, targetKey, ctrlKey = false, shiftKey = false, elementSelector = null) => {
    useEffect(() => {
        const keyPressHandler = event => {
            if (event.target.tagName.toLowerCase() === "input") return;
            if (event.key === targetKey) {
                if (((ctrlKey && event.ctrlKey) || !ctrlKey) && ((shiftKey && event.shiftKey) || !shiftKey)) {
                    event.preventDefault();
                    callback();
                }
            }
        };
        if (elementSelector) {
            const element = document.querySelector(elementSelector);
            if (element) {
                element.addEventListener("keydown", keyPressHandler);
                return () => element.removeEventListener("keydown", keyPressHandler);
            }
        } else {
            window.addEventListener("keydown", keyPressHandler);
            return () => window.removeEventListener("keydown", keyPressHandler);
        }
    }, [callback, ctrlKey, elementSelector, shiftKey, targetKey]);
};

export function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener("resize", updateSize);
        updateSize();
        return () => window.removeEventListener("resize", updateSize);
    }, []);
    return size;
}

export function useRadioName(radioId) {
    const { allRadioNames } = useRouteLoaderData("root");
    const [radioName, setRadioName] = useState(null);

    useEffect(() => {
        async function getRadioName() {
            const foundRadio = (await allRadioNames).data.find(x => x.radio_id === radioId);
            if (foundRadio) setRadioName(foundRadio.radio_name);
        }
        if (radioId && allRadioNames) getRadioName();
    }, [radioId, allRadioNames]);

    return radioName;
}

export function useStationsAvailable(condition) {
    const { radios } = useRouteLoaderData("root");
    const [stationsAvailable, setStationsAvailable] = useState(true);

    useEffect(() => {
        async function checkStationsAvailable() {
            setStationsAvailable((await radios).data.some(condition));
        }
        checkStationsAvailable();
    }, [radios, condition]);

    return stationsAvailable;
}

export function useStationsAvailableInModule(condition) {
    const { radiosModuleSettings } = useRouteLoaderData("root");
    const [stationsAvailable, setStationsAvailable] = useState(true);

    useEffect(() => {
        async function checkStationsAvailable() {
            setStationsAvailable((await radiosModuleSettings).data.some(condition));
        }
        checkStationsAvailable();
    }, [radiosModuleSettings, condition]);

    return stationsAvailable;
}

export function useSongCategoriesWithPerspectives(radioId) {
    const { songCategories } = useLoaderData();
    const [perspectivesWithCategories, setPerspectivesWithCategories] = useState([]);

    useEffect(() => {
        const setCategories = async () => {
            const categories = (await songCategories).data.filter(x => x.radio_id === radioId);
            const perspectivesWithCategories = playlistPerspectives.map(perspective => ({
                ...perspective,
                categories: categories.filter(x => perspective.categoryTypes.includes(x.category_type_id)),
                categoryGroups: categories
                    .filter(x => perspective.categoryTypes.includes(x.category_type_id))
                    .filter((x, i, arr) => arr.findIndex(obj => obj.code_group === x.code_group) === i)
                    .sort((a, b) => a.category_type_id - b.category_type_id || a.code_group - b.code_group),
            }));
            setPerspectivesWithCategories(perspectivesWithCategories);
        };
        setCategories();
    }, [songCategories, radioId]);

    return perspectivesWithCategories;
}

export function useResolvedRadios(includeCompetitors = false) {
    const { radios, competitorRadios } = useRouteLoaderData("root");
    const [resolvedRadios, setResolvedRadios] = useState(null);

    useEffect(() => {
        async function resolveRadios() {
            const resolvedRadios = includeCompetitors ? (await competitorRadios).data : (await radios).data;
            setResolvedRadios(resolvedRadios);
        }
        resolveRadios();
    }, [competitorRadios, includeCompetitors, radios]);

    return resolvedRadios;
}

export function useResolvedRadioNames() {
    const { allRadioNames } = useRouteLoaderData("root");
    const [resolvedRadios, setResolvedRadios] = useState(null);

    useEffect(() => {
        async function resolveRadios() {
            const resolvedRadios = (await allRadioNames).data;
            setResolvedRadios(resolvedRadios);
        }
        resolveRadios();
    }, [allRadioNames]);

    return resolvedRadios;
}

export function useResolvedModules() {
    const { modules } = useRouteLoaderData("root");
    const [resolvedModules, setResolvedModules] = useState(null);

    useEffect(() => {
        async function resolve() {
            const resolved = (await modules).data;
            setResolvedModules(resolved);
        }
        resolve();
    }, [modules]);

    return resolvedModules;
}

export function useResolvedRadioModuleSettings() {
    const { radiosModuleSettings } = useRouteLoaderData("root");
    const [resolved, setResolved] = useState(null);

    useEffect(() => {
        async function resolve() {
            setResolved((await radiosModuleSettings).data);
        }
        resolve();
    }, [radiosModuleSettings]);

    return resolved;
}

export function useFetchData() {
    const [loading, setLoading] = useState(false);
    const [errorCode, setErrorCode] = useState(null);
    const [data, setData] = useState(null);
    const controllerRef = useRef(new AbortController());

    const fetchData = useCallback(
        async (url, inputData) => {
            setLoading(true);
            controllerRef.current.abort();
            controllerRef.current = new AbortController();
            try {
                // Line below fixes a bug when loading is not shown if fetch is fast enough.
                await new Promise(resolve => setTimeout(resolve, 0));
                const response = inputData
                    ? await axiosInstance.post(url, inputData, { signal: controllerRef.current.signal })
                    : await axiosInstance.get(url, { signal: controllerRef.current.signal });
                setData(response.data);
                setErrorCode(null);
            } catch (error) {
                setData(null);
                if (error?.code !== "ERR_CANCELED") setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        },
        [setLoading, setData, setErrorCode]
    );

    return [loading, errorCode, data, fetchData, setData];
}

export function useEditableList({
    initialItems = [],
    idKey,
    itemName = "item",
    createTempItem,
    createItem,
    updateItem,
    deleteItem,
    onCreated,
    onUpdated,
    onDeleted,
    onError,
}) {
    if (!idKey) throw new Error("useEditableList: idKey is required");

    const [items, setItems] = useState(() => [...initialItems]);
    // store originals so discard can restore them
    const originalMapRef = useRef(new Map());

    // build key. can be string, function or list of strings
    const getKey = useMemo(() => {
        if (typeof idKey === "function") return idKey;
        if (Array.isArray(idKey)) return item => (item ? idKey.map(k => String(item[k] ?? "")).join("|") : "");
        return item => (item ? String(item[idKey] ?? "") : "");
    }, [idKey]);
    const keyOf = useCallback(item => getKey(item), [getKey]);

    // initialize originalMap when initialItems changes
    useEffect(() => {
        originalMapRef.current = new Map(initialItems.map(i => [getKey(i), i]));
        setItems([...initialItems]);
    }, [initialItems, getKey]);

    // default new item when adding
    const defaultCreateTempItem = useCallback(
        curr => {
            const tmp = -(curr.length + 1);
            if (typeof idKey === "string") return { [idKey]: tmp, status: "ADD" };
            if (Array.isArray(idKey)) return { [idKey[0]]: tmp, status: "ADD" };
            return { __tempKey: tmp, status: "ADD" };
        },
        [idKey]
    );
    const makeTempItem = createTempItem || defaultCreateTempItem;

    // Local Edits
    const add = useCallback(props => setItems(curr => [...curr, makeTempItem(curr, props)]), [makeTempItem]);
    const change = useCallback((idObj, key, value) => setItems(c => c.map(x => (keyOf(x) === keyOf(idObj) ? setNestedProperty(x, key, value) : x))), [keyOf]);
    const toggleEdit = useCallback(
        idObj =>
            setItems(c =>
                c.map(x => (keyOf(x) === keyOf(idObj) ? { ...x, status: x.status === "EDIT" || x.status === "ADD" ? x.status : x.status ? null : "EDIT" } : x))
            ),
        [keyOf]
    );
    // restore original or drop if new
    const discard = useCallback(
        idObj => {
            const id = keyOf(idObj);
            const original = originalMapRef.current.get(id);
            setItems(c => (original ? c.map(x => (keyOf(x) === id ? original : x)) : c.filter(x => keyOf(x) !== id)));
        },
        [keyOf]
    );
    const hasChanged = useCallback(
        idObj => {
            const id = keyOf(idObj);
            const original = originalMapRef.current.get(id);
            if (!original) return true;
            const current = items.find(x => keyOf(x) === id);
            return JSON.stringify(original) !== JSON.stringify(current);
        },
        [items, keyOf]
    );
    // call deleteItem if provided, otherwise just remove locally
    const remove = useCallback(
        async idObj => {
            try {
                if (deleteItem) {
                    await toast.promise(deleteItem(idObj), {
                        pending: `Deleting ${itemName}...`,
                        success: `Successfully deleted ${itemName}!`,
                        error: {
                            render({ data }) {
                                const message = getErrMsg(data);
                                return (
                                    <div>
                                        <h6>Failed to delete {itemName}!</h6>
                                        <span className="fs-7 text-muted">{message}</span>
                                    </div>
                                );
                            },
                        },
                    });
                }
                setItems(c => c.filter(x => keyOf(x) !== keyOf(idObj)));
                originalMapRef.current.delete(keyOf(idObj));
                onDeleted?.();
            } catch (err) {
                (onError || console.error)("useEditableList.remove:", err);
            }
        },
        [deleteItem, keyOf, onDeleted, itemName, onError]
    );

    // SAVE (create or update depending on status)
    const save = useCallback(
        async item => {
            const id = keyOf(item);
            try {
                if (item.status === "ADD") {
                    if (!createItem) throw new Error("createItem is required for saving new items");
                    const created = await toast.promise(createItem(item), {
                        pending: `Creating ${itemName}...`,
                        success: `Successfully created ${itemName}!`,
                        error: {
                            render({ data }) {
                                return (
                                    <div>
                                        <h6>Failed to create {itemName}!</h6>
                                        <span className="fs-7 text-muted">{getErrMsg(data)}</span>
                                    </div>
                                );
                            },
                        },
                    });
                    const createdReset = { ...created, status: null };
                    setItems(c => c.map(x => (keyOf(x) === id ? createdReset : x)));
                    originalMapRef.current.set(getKey(created), createdReset);
                    onCreated?.(createdReset);
                } else {
                    if (!updateItem) throw new Error("updateItem is required for updating items");
                    const updated =
                        (await toast.promise(updateItem(item), {
                            pending: `Updating ${itemName}...`,
                            success: `Successfully updated ${itemName}!`,
                            error: {
                                render({ data }) {
                                    const message = getErrMsg(data);
                                    return (
                                        <div>
                                            <h6>Failed to update {itemName}!</h6>
                                            <span className="fs-7 text-muted">{message}</span>
                                        </div>
                                    );
                                },
                            },
                        })) ?? item;
                    const updatedReset = { ...updated, status: null };
                    setItems(c => c.map(x => (keyOf(x) === id ? updatedReset : x)));
                    originalMapRef.current.set(getKey(updated), updatedReset);
                    onUpdated?.(updatedReset);
                }
            } catch (err) {
                (onError || console.error)("useEditableList.save:", err);
            }
        },
        [getKey, keyOf, createItem, itemName, onCreated, updateItem, onUpdated, onError]
    );

    const handlers = useMemo(
        () => ({ add, change, toggleEdit, remove, save, discard, hasChanged }),
        [add, change, toggleEdit, remove, save, discard, hasChanged]
    );

    return { items, setItems, handlers, originalMapRef };
}
